import { ConversationRestrictionsData, GroupInterface, VarKey } from "@swivl-mono/types";
import { Spinner } from "@swivl-mono/ui";
import { useEffect } from "react";

import LocationsModel from "../../Models/Locations/Locations.Model";
import { AlertsModel } from "../../Models/Notifications/Alerts.Model";
import { WidgetSwitch } from "../../Views/Widgets/WidgetSwitch";
import LocationsAndGroupsDropdown, { LocationsAndGroupsDropdownType } from "../Dropdowns/LocationsAndGroupsDropdown/LocationsAndGroupsDropdown";

interface ConversationRestrictionsControlsProps {
  restrictionsData: ConversationRestrictionsData;
  updateRestrictionsData: (data: ConversationRestrictionsData) => void;
  disabled?: boolean;
}

export function ConversationRestrictionsControls(props: ConversationRestrictionsControlsProps) {
  const { restrictionsData, updateRestrictionsData, disabled } = props;
  const { groups } = LocationsModel.useState((s) => s);

  useEffect(() => {
    LocationsModel.getGroupsAndLocations(false);
  }, []);

  const { defaultGroup, startOnLocation, limitedToGroupId } = restrictionsData;
  const limitToGroup = limitedToGroupId ? true : false;
  const startOnLocationActive = startOnLocation ? true : false;
  if (!groups || !groups.length) {
    return (
      <div className="ConversationRestrictionsControls">
        <label>Default Settings Group</label>
        <Spinner />
      </div>
    );
  }

  const groupName = (groupId: number | undefined): string => groups.find((g) => g.id === groupId)?.name ?? "—";
  const locationName = (locationId: number | undefined): string =>
    groups.flatMap((g) => g.locations ?? []).find((l) => l.id === locationId)?.name ?? "—";

  return (
    <div className="ConversationRestrictionsControls">
      <label>Default Settings Group</label>

      {disabled ? (
        <p className="body-sm text-grey-900">{groupName(defaultGroup)}</p>
      ) : (
        <LocationsAndGroupsDropdown
          type={LocationsAndGroupsDropdownType.GROUPS}
          deselectLabel={"None"}
          onSelect={(groupId, _) => {
            let data: any = { ...restrictionsData };
            if (!groupId) {
              delete data.defaultGroup;
            } else {
              data.defaultGroup = groupId;
            }
            updateRestrictionsData(data);
          }}
          groupId={defaultGroup || undefined}
        />
      )}

      <div className="my-3">
        <label>Limit to Group</label>
        <div className="mb-2 flex items-center">
          <WidgetSwitch
            on={limitToGroup}
            disabled={disabled}
            ariaLabel="Limit to Group"
            onSet={(checked) => {
              let data: any = { ...restrictionsData };
              if (!checked) {
                delete data.limitedToGroupId;
              } else {
                if (!groups[0]?.id && !startOnLocation) {
                  AlertsModel.errorToast("No Groups found");
                  return;
                }
                data.limitedToGroupId = limitedToGroupId || groups[0]?.id || 0;
              }
              updateRestrictionsData(data);

              // let preloadVariables:any = embed.preloadVariables || {};

              // if (!checked) {
              //   delete preloadVariables[VarKey.UserLimitToGroupId]
              // } else {
              //   preloadVariables[VarKey.UserLimitToGroupId] = groups && Object.values(groups).length ? String(Object.values(groups)[0].id) : ""
              // }
              // changeKey('preloadVariables', preloadVariables)
            }}
          />
        </div>
        {limitToGroup &&
          (disabled ? (
            <p className="body-sm text-grey-900">{groupName(limitedToGroupId)}</p>
          ) : (
            <LocationsAndGroupsDropdown
              type={LocationsAndGroupsDropdownType.GROUPS}
              onSelect={(groupId, _) => {
                let data: any = { ...restrictionsData };
                if (!groupId) {
                  delete data.limitedToGroupId;
                } else {
                  data.limitedToGroupId = groupId;
                }
                updateRestrictionsData(data);
              }}
              groupId={limitedToGroupId || undefined}
            />
          ))}
      </div>

      <div className="my-3">
        <label>Start On Specific Location</label>
        <div className="mb-2 flex items-center">
          <WidgetSwitch
            on={startOnLocationActive}
            disabled={disabled}
            ariaLabel="Start On Specific Location"
            onSet={(checked) => {
              let data: any = { ...restrictionsData };
              if (!checked) {
                delete data.startOnLocation;
              } else {
                const firstLocation = groups?.flatMap((g) => g.locations ?? []).find((l) => l.id)?.id;
                if (!firstLocation && !startOnLocation) {
                  AlertsModel.errorToast("No locations found");
                  return;
                }
                data.startOnLocation = startOnLocation || firstLocation || 0;
              }
              updateRestrictionsData(data);
            }}
          />
        </div>
        {startOnLocation &&
          (disabled ? (
            <p className="body-sm text-grey-900">{locationName(startOnLocation)}</p>
          ) : (
            <LocationsAndGroupsDropdown
              type={LocationsAndGroupsDropdownType.LOCATIONS}
              onSelect={(_, locationId) => {
                let data: any = { ...restrictionsData };
                if (!locationId) {
                  delete data.startOnLocation;
                } else {
                  data.startOnLocation = locationId;
                }
                updateRestrictionsData(data);
              }}
              locationId={startOnLocation || undefined}
            />
          ))}
      </div>
    </div>
  );
}
